import useConsentCookie from '../hooks/useConsentCookie';
import { useCallback } from 'react';
import { track as amplitudeTrack } from '@amplitude/analytics-browser';

interface TrackExperimentEventAnonProps {
  metricKey: string;
  audienceAttributes?: { [name: string]: any };
  groups?: Record<string, string | string[]>; // Optional, if any grouping is needed
}

/**
 * @description Use for tracking metrics defined in Amplitude on unauthenticated pages (no user context).
 * Only tracks if user has given consent.
 * @returns {() => void} A callback function to track the metric event.
 * @example <caption>Track clicks on a button</caption>
 * const trackClick = useTrackExperimentEventAnon({ metricKey: 'landing_clicked' });
 * <button onClick={trackClick}>click me</button>
 */
export const useTrackExperimentEventAnon = ({
  metricKey,
  audienceAttributes,
  groups
}: TrackExperimentEventAnonProps): (() => void) => {
  const hasConsent = useConsentCookie();

  const trackExperimentEvent = useCallback(() => {
    if (!hasConsent) return;

    amplitudeTrack({
      event_type: metricKey,
      event_properties: audienceAttributes,
      groups
    });
  }, [hasConsent, metricKey, audienceAttributes, groups]);

  return trackExperimentEvent;
};
